import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useOfflineMutation } from '@/hooks/useOfflineMutation';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

interface Consultation {
  id: string;
  patient_id: string;
  medecin_id: string;
  rendez_vous_id: string | null;
  date_consultation: string;
  heure_debut: string | null;
  heure_fin: string | null;
  diagnostic: string | null;
  statut: string;
  created_at: string;
  updated_at: string;
  patients: {
    id: string;
    nom: string;
    prenom: string;
    numero_dossier: string;
  } | null;
  medecins: {
    id: string;
    nom: string;
    prenom: string;
    specialite: string;
  } | null;
}

export default function AdminConsultationsPage() {
  const { user, role, userRoles, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isTrueSuperAdmin = userRoles?.includes('super_admin') ?? false;
  const [loading, setLoading] = useState(true);
  const [consultations, setConsultations] = useState<Consultation[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const { mutate, state: mutationState } = useOfflineMutation<any>();

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    if (role !== 'super_admin' && role !== 'admin') { navigate('/', { replace: true }); return; }
    loadConsultations();
  }, [user, role, authLoading]);

  const loadConsultations = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('consultations')
        .select('*, patients(id, nom, prenom, numero_dossier), medecins(id, nom, prenom, specialite)')
        .order('created_at', { ascending: false })
        .limit(500);
      if (data) setConsultations(data);
    } catch (err) {
      console.error('Erreur chargement consultations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTerminerConsultation = async (cons: Consultation) => {
    if (!confirm(`Terminer la consultation de ${cons.patients?.prenom} ${cons.patients?.nom} ?`)) return;
    setActionLoading(cons.id);
    try {
      const now = new Date();
      await mutate({
        table: 'consultations',
        operation: 'UPDATE',
        recordId: cons.id,
        data: { statut: 'termine', heure_fin: now.toTimeString().slice(0, 8), updated_at: now.toISOString() },
        onlineExecutor: async () => {
          const { data, error } = await supabase.from('consultations').update({
            statut: 'termine',
            heure_fin: now.toTimeString().slice(0, 8),
            updated_at: now.toISOString(),
          }).eq('id', cons.id).select().single();
          if (error) throw error;
          return data;
        },
        metadata: { summary: `Fermeture consultation ${cons.patients?.nom} ${cons.patients?.prenom}` },
      });
      await loadConsultations();
    } catch (err) {
      console.error('Erreur:', err);
      alert('Erreur lors de la terminaison');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSupprimerConsultation = async (cons: Consultation) => {
    if (!confirm(`ATTENTION : Supprimer définitivement la consultation de ${cons.patients?.prenom} ${cons.patients?.nom} du ${new Date(cons.date_consultation).toLocaleDateString('fr-FR')} ?\n\nLes prescriptions, bons de laboratoire et fiches de soins associés resteront en base.`)) return;
    setActionLoading(cons.id);
    try {
      await mutate({
        table: 'consultations',
        operation: 'DELETE',
        recordId: cons.id,
        onlineExecutor: async () => {
          const { error } = await supabase.from('consultations').delete().eq('id', cons.id);
          if (error) throw error;
          return { deleted: true };
        },
        metadata: { summary: `Suppression consultation ${cons.patients?.nom} ${cons.patients?.prenom}` },
      });
      await loadConsultations();
    } catch (err) {
      console.error('Erreur:', err);
      alert('Erreur lors de la suppression');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatutBadge = (statut: string) => {
    switch (statut) {
      case 'en_cours': return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 whitespace-nowrap">En cours</span>;
      case 'termine': return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 whitespace-nowrap">Terminée</span>;
      default: return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 whitespace-nowrap">{statut}</span>;
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const filteredCons = consultations.filter((c) => {
    const search = searchTerm.toLowerCase();
    const patientMatch = c.patients ? `${c.patients.nom} ${c.patients.prenom}`.toLowerCase().includes(search) : false;
    const medecinMatch = c.medecins ? `dr ${c.medecins.nom} ${c.medecins.prenom}`.toLowerCase().includes(search) : false;
    const diagMatch = c.diagnostic ? c.diagnostic.toLowerCase().includes(search) : false;
    const dossierMatch = c.patients?.numero_dossier?.toLowerCase().includes(search) || false;

    if (search && !patientMatch && !medecinMatch && !diagMatch && !dossierMatch) return false;
    if (filterStatut && c.statut !== filterStatut) return false;
    return true;
  });

  const stats = {
    total: consultations.length,
    enCours: consultations.filter((c) => c.statut === 'en_cours').length,
    terminees: consultations.filter((c) => c.statut === 'termine').length,
    aujourdhui: consultations.filter((c) => c.date_consultation === new Date().toISOString().split('T')[0]).length,
    sansRdv: consultations.filter((c) => !c.rendez_vous_id).length,
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin mb-4 block"></i>
          <div className="text-gray-700 text-xl">Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/admin')} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-all cursor-pointer">
                <i className="ri-arrow-left-line text-xl text-gray-600"></i>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Gestion des Consultations</h1>
                <p className="mt-1 text-gray-600">Superviser, fermer ou supprimer les consultations de tous les médecins</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white p-5 rounded-2xl border border-gray-100">
              <p className="text-sm text-gray-500 mb-1">Total</p>
              <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-orange-100">
              <p className="text-sm text-gray-500 mb-1">En cours</p>
              <p className="text-3xl font-bold text-orange-600">{stats.enCours}</p>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-green-100">
              <p className="text-sm text-gray-500 mb-1">Terminées</p>
              <p className="text-3xl font-bold text-green-600">{stats.terminees}</p>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-blue-100">
              <p className="text-sm text-gray-500 mb-1">Aujourd'hui</p>
              <p className="text-3xl font-bold text-blue-600">{stats.aujourdhui}</p>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-red-100">
              <p className="text-sm text-gray-500 mb-1">Sans RDV</p>
              <p className="text-3xl font-bold text-red-500">{stats.sansRdv}</p>
            </div>
          </div>

          {/* Filtres */}
          <div className="bg-white p-6 rounded-2xl border border-gray-100 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher par patient, médecin, diagnostic ou n° dossier..."
                  className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-teal-500 text-sm"
                />
              </div>
              <select
                value={filterStatut}
                onChange={(e) => setFilterStatut(e.target.value)}
                className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-teal-500 text-sm cursor-pointer min-w-[180px]"
              >
                <option value="">Tous les statuts</option>
                <option value="en_cours">En cours</option>
                <option value="termine">Terminée</option>
              </select>
            </div>
            {stats.sansRdv > 0 && (
              <div className="mt-4 p-3 bg-teal-50 border border-teal-200 rounded-xl">
                <p className="text-sm text-teal-800 flex items-center gap-2">
                  <i className="ri-information-line w-4 h-4 flex items-center justify-center"></i>
                  <strong>{stats.sansRdv}</strong> consultation(s) réalisée(s) en « consultation directe » (sans rendez-vous programmé).
                </p>
              </div>
            )}
          </div>

          {/* Liste */}
          <div className="bg-white p-8 rounded-3xl border border-gray-100">
            {filteredCons.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-teal-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className="ri-stethoscope-line text-4xl text-teal-500"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Aucune consultation trouvée</h3>
                <p className="text-gray-500">Essayez de modifier vos filtres</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredCons.map((cons) => (
                  <div
                    key={cons.id}
                    className={`p-6 rounded-2xl border-2 transition-all ${
                      cons.statut === 'en_cours'
                        ? 'border-orange-300 bg-orange-50/20'
                        : 'border-gray-200 hover:border-teal-500/20'
                    }`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                            cons.statut === 'en_cours' ? 'bg-orange-500' : 'bg-green-500'
                          }`}>
                            <i className={`text-lg text-white ${cons.statut === 'en_cours' ? 'ri-time-line' : 'ri-check-line'}`}></i>
                          </div>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-lg font-bold text-gray-900">
                                {cons.patients ? `${cons.patients.prenom} ${cons.patients.nom}` : 'Patient supprimé'}
                              </span>
                              {cons.patients?.numero_dossier && (
                                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                                  N°{cons.patients.numero_dossier}
                                </span>
                              )}
                              {!cons.rendez_vous_id && (
                                <span className="text-xs text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full flex items-center gap-1">
                                  <i className="ri-user-heart-line w-3 h-3 flex items-center justify-center"></i>
                                  Consultation directe
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mt-0.5">
                              {cons.medecins ? `Dr. ${cons.medecins.nom} ${cons.medecins.prenom}` : 'Médecin supprimé'}
                              {cons.medecins?.specialite ? ` — ${cons.medecins.specialite}` : ''}
                            </p>
                          </div>
                        </div>

                        <div className="ml-13 grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="flex items-center gap-2">
                            <i className="ri-calendar-line text-teal-500 w-4 h-4 flex items-center justify-center"></i>
                            <p className="text-sm text-gray-700">
                              <strong>Date :</strong> {formatDate(cons.date_consultation)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <i className="ri-time-line text-teal-500 w-4 h-4 flex items-center justify-center"></i>
                            <p className="text-sm text-gray-700">
                              <strong>Début :</strong> {cons.heure_debut?.substring(0, 5) || '—'}
                              {cons.heure_fin ? ` → Fin : ${cons.heure_fin.substring(0, 5)}` : ''}
                            </p>
                          </div>
                          {cons.diagnostic && (
                            <div className="flex items-start gap-2 md:col-span-2">
                              <i className="ri-mental-health-line text-red-400 w-4 h-4 flex items-center justify-center mt-0.5"></i>
                              <p className="text-sm text-gray-600 line-clamp-2">{cons.diagnostic}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-3">
                        {getStatutBadge(cons.statut)}
                        <div className="text-xs text-gray-400">
                          Créée le {new Date(cons.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </div>

                        <div className="flex flex-wrap gap-2 justify-end">
                          {cons.statut === 'en_cours' && (
                            <button
                              onClick={() => handleTerminerConsultation(cons)}
                              disabled={actionLoading === cons.id}
                              className="flex items-center gap-1 px-4 py-2 bg-green-50 text-green-700 rounded-full text-sm font-medium hover:bg-green-100 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50"
                            >
                              {actionLoading === cons.id ? (
                                <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>
                              ) : (
                                <i className="ri-check-line w-4 h-4 flex items-center justify-center"></i>
                              )}
                              Fermer
                            </button>
                          )}
                          {isTrueSuperAdmin && (
                            <button
                              onClick={() => handleSupprimerConsultation(cons)}
                              disabled={actionLoading === cons.id}
                              className="flex items-center gap-1 px-4 py-2 bg-red-50 text-red-700 rounded-full text-sm font-medium hover:bg-red-100 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50"
                            >
                              {actionLoading === cons.id ? (
                                <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>
                              ) : (
                                <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                              )}
                              Supprimer
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}